/**
 * Composant SEO - Gère les meta tags dynamiques
 * Utilisation: <SEOHead title="Ma Page" description="..." />
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import brand from '../../config/brand';

// Configuration par défaut (pilotée par la marque active - white-label)
const DEFAULT_CONFIG = {
  siteName: brand.name,
  defaultTitle: `${brand.name} - ${brand.tagline}`,
  defaultDescription: brand.key === 'dna'
    ? 'DNA-24 — Worldwide medical & travel assistance and concierge services for third parties across Asia, on behalf of local and international insurance partners.'
    : 'ASAR CSG, votre courtier en assurances à votre service. Particuliers, professionnels, expatriés - Solutions sur mesure pour tous vos besoins d\'assurance.',
  defaultImage: brand.logo,
  twitterHandle: brand.key === 'dna' ? '@dna24' : '@asarcsg',
  locale: (brand.defaultLocale === 'en') ? 'en_US' : 'fr_FR',
};

// Configuration SEO par page (nom de marque interpolé - white-label)
const PAGE_SEO = {
  '/': {
    title: 'Accueil',
    description: `${brand.name}, courtier en assurances. Solutions personnalisées pour particuliers, professionnels et expatriés. Devis gratuit et accompagnement sur mesure.`,
  },
  '/about': {
    title: 'À Propos',
    description: `Découvrez ${brand.name}, votre courtier en assurances de confiance. Notre histoire, nos valeurs et notre engagement à vos côtés.`,
  },
  '/team': {
    title: 'Notre Équipe',
    description: `Rencontrez l'équipe ${brand.name}. Des experts en assurance dédiés à vous accompagner dans tous vos projets.`,
  },
  '/contact': {
    title: 'Contact',
    description: `Contactez ${brand.name}. Notre équipe est à votre écoute pour répondre à toutes vos questions sur l'assurance.`,
  },
  '/rendez-vous': {
    title: 'Prendre Rendez-vous',
    description: `Prenez rendez-vous avec un conseiller ${brand.name}. Consultation gratuite et sans engagement.`,
  },
  '/solutions/particuliers': {
    title: 'Assurances Particuliers',
    description: 'Solutions d\'assurance pour particuliers : auto, habitation, santé, prévoyance. Protégez ce qui compte.',
  },
  '/solutions/professionnels': {
    title: 'Assurances Professionnels',
    description: 'Assurances pour professionnels et entreprises. RC Pro, multirisque, flotte auto. Solutions adaptées à votre activité.',
  },
  '/solutions/expatries': {
    title: 'Assurances Expatriés',
    description: 'Assurance santé et prévoyance pour expatriés. Couverture mondiale, assistance 24/7.',
  },
  '/solutions/voyage': {
    title: 'Assurance Voyage',
    description: 'Assurance voyage et assistance. Partez l\'esprit tranquille avec une couverture complète.',
  },
  '/demande-etude': {
    title: 'Demande d\'Étude',
    description: 'Demandez une étude personnalisée de vos besoins en assurance. Gratuit et sans engagement.',
  },
  '/informations-legales': {
    title: 'Mentions Légales',
    description: `Mentions légales et informations juridiques de ${brand.name}.`,
  },
  '/rgpd': {
    title: 'Politique de Confidentialité',
    description: `Politique de confidentialité et protection des données personnelles ${brand.name}.`,
  },
  '/cookies': {
    title: 'Politique des Cookies',
    description: `Politique d'utilisation des cookies sur le site ${brand.name}.`,
  },
};

const SEOHead = ({ 
  title, 
  description, 
  image,
  type = 'website',
  noIndex = false,
}) => {
  const location = useLocation();
  const pathname = location.pathname;
  
  // Récupérer la config de la page ou utiliser les valeurs par défaut
  const pageConfig = PAGE_SEO[pathname] || {};
  
  const finalTitle = title || pageConfig.title 
    ? `${title || pageConfig.title} | ${DEFAULT_CONFIG.siteName}`
    : DEFAULT_CONFIG.defaultTitle;
    
  const finalDescription = description || pageConfig.description || DEFAULT_CONFIG.defaultDescription;
  const finalImage = image || DEFAULT_CONFIG.defaultImage;
  const canonicalUrl = `${brand.websiteUrl}${pathname}`;
  
  useEffect(() => {
    // Title
    document.title = finalTitle;
    
    // Meta description
    updateMetaTag('name', 'description', finalDescription);
    
    // Robots
    if (noIndex) {
      updateMetaTag('name', 'robots', 'noindex, nofollow');
    } else {
      updateMetaTag('name', 'robots', 'index, follow');
    }
    
    // Canonical
    updateLinkTag('canonical', canonicalUrl);
    
    // Open Graph
    updateMetaTag('property', 'og:title', finalTitle);
    updateMetaTag('property', 'og:description', finalDescription);
    updateMetaTag('property', 'og:image', finalImage);
    updateMetaTag('property', 'og:url', canonicalUrl);
    updateMetaTag('property', 'og:type', type);
    updateMetaTag('property', 'og:site_name', DEFAULT_CONFIG.siteName);
    updateMetaTag('property', 'og:locale', DEFAULT_CONFIG.locale);
    
    // Twitter Cards
    updateMetaTag('name', 'twitter:card', 'summary_large_image');
    updateMetaTag('name', 'twitter:title', finalTitle);
    updateMetaTag('name', 'twitter:description', finalDescription);
    updateMetaTag('name', 'twitter:image', finalImage);
    updateMetaTag('name', 'twitter:site', DEFAULT_CONFIG.twitterHandle);
    
  }, [finalTitle, finalDescription, finalImage, canonicalUrl, type, noIndex]);
  
  return null; // Ce composant ne rend rien visuellement
};

// Utilitaire pour mettre à jour les meta tags
function updateMetaTag(attribute, key, content) {
  let element = document.querySelector(`meta[${attribute}="${key}"]`);
  
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  
  element.setAttribute('content', content);
}

// Utilitaire pour mettre à jour les link tags
function updateLinkTag(rel, href) {
  let element = document.querySelector(`link[rel="${rel}"]`);
  
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  
  element.setAttribute('href', href);
}

export default SEOHead;
